import React from "react";
import { BookOpen, Link as LinkIcon, Users, Trophy, Info } from "lucide-react";
import SectionCard from "Explore/Pages/Overview/Components/SectionCard";
import { Skeleton } from "components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";

const formatCompact = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "N/A";
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(num);
};

const getTimeRangeLabel = (yearRange, subjectArea) => {
  let timeText = "";
  if (yearRange === "last_5_years") timeText = "last 5 years";
  else if (yearRange === "last_10_years") timeText = "last 10 years";
  else if (yearRange === "all") timeText = "all indexed years";

  const subjectText =
    Array.isArray(subjectArea) && subjectArea.length > 0 ? subjectArea[0] : null;

  if (subjectText && timeText) {
    return `Core scholarly output and citation metrics for ${subjectText} (${timeText})`;
  }
  if (subjectText) {
    return `Core scholarly output and citation metrics for ${subjectText}`;
  }
  if (timeText) {
    return `Core scholarly output and citation metrics for the ${timeText}`;
  }
  return "Core scholarly output, citation impact, and active researchers";
};

const MetricsSummaryCard = ({
  kpis,
  yearRange,
  subjectArea,
  isLoading,
  error,
  isHidden,
  onRetry,
}) => {
  const stats = [
    {
      id: "publications",
      label: "Publications",
      value: kpis?.publications,
      icon: BookOpen,
      tooltip:
        "Total peer-reviewed and indexed research papers published within the selected timeframe.",
      getSubtext: (val) =>
        val != null ? `${Number(val).toLocaleString()} total papers` : "Indexed publications",
    },
    {
      id: "citations",
      label: "Citations",
      value: kpis?.citations,
      icon: LinkIcon,
      tooltip:
        "Total citations received across all published research outputs within the selected timeframe.",
      getSubtext: (val) =>
        val != null ? `${Number(val).toLocaleString()} total citations` : "Citation volume",
    },
    {
      id: "researchers",
      label: "Researchers",
      value: kpis?.researchers,
      icon: Users,
      tooltip:
        "Total contributing faculty, researchers, and affiliated scholars associated with published outputs.",
      getSubtext: (val) =>
        val != null ? `${Number(val).toLocaleString()} contributing authors` : "Affiliated scholars",
    },
    {
      id: "h_index",
      label: "H-Index",
      value: kpis?.h_index,
      icon: Trophy,
      tooltip:
        "Institutional productivity and citation impact: at least H papers have received at least H citations each.",
      getSubtext: (val) =>
        val != null ? `Impact factor benchmark (${val})` : "Citation benchmark",
    },
  ];

  const description = getTimeRangeLabel(yearRange, subjectArea);

  return (
    <SectionCard
      title="Key Performance Indicators"
      description={description}
      error={error}
      isHidden={isHidden}
      isEmpty={!isLoading && !kpis}
      onRetry={onRetry}
    >
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="p-4 sm:p-5 rounded-xl border border-slate-200/80 bg-white flex flex-col justify-between h-[122px]"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Skeleton className="w-8 h-8 rounded-lg" />
                  <Skeleton className="w-20 h-3 rounded" />
                </div>
                <Skeleton className="w-3.5 h-3.5 rounded-full" />
              </div>
              <Skeleton className="w-24 h-7 rounded my-2" />
              <Skeleton className="w-32 h-3 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            const hasData =
              stat.value !== undefined && stat.value !== null && !isNaN(stat.value);
            const displayValue = hasData ? formatCompact(stat.value) : "—";
            const subtext = hasData ? stat.getSubtext(stat.value) : "No data available";
            const fullValue = hasData ? Number(stat.value).toLocaleString() : null;

            return (
              <div
                key={stat.id || idx}
                className="relative flex flex-col justify-between p-4 sm:p-5 rounded-xl border border-slate-200/80 bg-white hover:border-explore/40 hover:shadow-xs transition-all duration-200 group"
              >
                {/* Header row: Icon & Label on Left, Tooltip Info on Right */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-explore/5 border border-explore/10 flex items-center justify-center text-explore shrink-0 group-hover:bg-explore group-hover:text-white group-hover:border-explore transition-all duration-200 shadow-2xs">
                      <Icon size={15} className="stroke-[2.2]" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 group-hover:text-slate-800 transition-colors truncate">
                      {stat.label}
                    </span>
                  </div>

                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`About ${stat.label}`}
                          className="text-slate-400 hover:text-explore transition-colors cursor-help p-1 rounded-full hover:bg-slate-100 shrink-0"
                        >
                          <Info size={13} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs font-normal">
                        {stat.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Metric Value */}
                <div className="mt-3.5 mb-1">
                  <span
                    className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 group-hover:text-explore transition-colors tabular-nums font-sans"
                    title={fullValue ? `${fullValue} ${stat.label}` : undefined}
                  >
                    {displayValue}
                  </span>
                </div>

                {/* Subtext */}
                <div className="text-xs text-slate-500 font-medium truncate" title={subtext}>
                  {subtext}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

export default MetricsSummaryCard;
